// Contractor ↔ employee overlap. A payee who is also an active employee of the
// same company can't be paid through Contractor & External Pay: a consultancy
// fee to your own employee is usually taxed as salary (PAYE), not 5% WHT. To pay
// anyway, someone records why and attaches evidence, and a DIFFERENT person
// approves — the decision and who made it stay on record.
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FileText, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Label } from '@/features/shared/components/ui/label';
import { Textarea } from '@/features/shared/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/features/shared/components/ui/dialog';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { useAppSelector } from '@/app/hooks';
import { getApiErrorMessage } from '@/lib/apiError';
import { commissionApi, type CommissionPayee, type EmployeeOverlap, type OverlapClearance } from '../api/commissionApi';

export const OVERLAP_WARNING = 'This person is on payroll — consultancy fees to employees are usually taxed as salary (PAYE), not 5% WHT.';
export const MIN_REASON = 20;
const MAX_EVIDENCE_BYTES = 15 * 1024 * 1024;
const EVIDENCE_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];

const BASIS: Record<string, string> = { person: 'same person', tin: 'same TIN', national_id: 'same national ID', email: 'same email' };

const STATUS: Record<OverlapClearance['status'], { label: string; variant: 'success' | 'warning' | 'danger' | 'secondary' }> = {
  pending: { label: 'Awaiting a second approver', variant: 'warning' },
  approved: { label: 'Cleared to pay', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'danger' },
  revoked: { label: 'Revoked', variant: 'secondary' },
};

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '');

export function EmployeeOverlapDialog({ payee, overlap, onClose, onChanged }: {
  payee: CommissionPayee;
  overlap: EmployeeOverlap;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { canRunPayroll, canApprovePayroll } = useRoleAccess();
  const myUserId = useAppSelector((s) => s.auth?.user?.id) as string | undefined;
  const [reason, setReason] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const [noteMissing, setNoteMissing] = useState(false);
  const [busy, setBusy] = useState(false);

  const c = overlap.clearance;
  const open = c && (c.status === 'pending' || c.status === 'approved');
  const canSubmit = canRunPayroll && overlap.matches.length > 0 && !open;
  const iSubmitted = !!c && !!myUserId && c.submitted_by === myUserId;

  const fileError = file && (!EVIDENCE_TYPES.includes(file.type)
    ? 'Attach a PDF, PNG or JPEG.'
    : file.size > MAX_EVIDENCE_BYTES ? 'The file is larger than 15 MB.' : null);
  const reasonShort = reason.trim().length < MIN_REASON;

  const submit = async () => {
    if (!file || fileError || reasonShort) return;
    setBusy(true);
    try {
      await commissionApi.submitOverlapClearance(payee.payee_id, reason.trim(), file);
      toast.success('Clearance submitted — a different approver must now approve it');
      onChanged();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e, 'Could not submit the clearance'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (action: 'approve' | 'reject' | 'revoke') => {
    if (!c) return;
    // The form's own check, shown at the field: a toast is for the server's answer.
    if (action !== 'approve' && !note.trim()) { setNoteMissing(true); return; }
    setBusy(true);
    try {
      await commissionApi.overlapClearanceAction(c.id, action, note.trim());
      toast.success(action === 'approve' ? 'Clearance approved — payment can go ahead' : action === 'reject' ? 'Clearance rejected' : 'Clearance revoked — payments are blocked again');
      onChanged();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e, 'Could not update the clearance'));
    } finally {
      setBusy(false);
    }
  };

  const openEvidence = async () => {
    if (!c) return;
    try {
      window.open(await commissionApi.overlapClearanceEvidenceUrl(c.id), '_blank', 'noopener,noreferrer');
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e, 'Could not open the evidence'));
    }
  };

  return (
    <Dialog open onOpenChange={(x) => !x && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Also on payroll — {payee.legal_name}</DialogTitle></DialogHeader>

        <div className={`rounded-md border p-3 ${overlap.blocked ? 'border-danger bg-danger-soft' : 'border-success bg-success-soft'}`}>
          <div className="flex items-center gap-2 font-medium text-foreground">
            {overlap.blocked ? <ShieldAlert className="h-4 w-4" aria-hidden="true" /> : <ShieldCheck className="h-4 w-4" aria-hidden="true" />}
            {overlap.blocked ? 'Payment blocked' : 'Cleared to pay'}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{overlap.warning ?? OVERLAP_WARNING}</p>
          {overlap.blocked && (
            <p className="mt-1 text-sm text-muted-foreground">
              Usually the right fix is to add the fee to their payroll instead. Pay it here only for genuinely separate work, with a reason and evidence that a second person approves.
            </p>
          )}
        </div>

        <div>
          <div className="mb-1 text-sm font-medium text-foreground">Matching employee records</div>
          <ul className="space-y-1">
            {overlap.matches.map((m) => (
              <li key={m.employee_id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm">
                <span className="text-foreground">{m.full_name}</span>
                <span className="text-xs text-muted-foreground">{m.basis.map((b) => BASIS[b] ?? b).join(' · ')}</span>
              </li>
            ))}
          </ul>
        </div>

        {c && (
          <div className="space-y-2 rounded-md border border-border p-3 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-foreground">Clearance</span>
              <Badge variant={STATUS[c.status].variant}>{STATUS[c.status].label}</Badge>
            </div>
            <p className="whitespace-pre-wrap text-foreground">{c.reason}</p>
            <button type="button" onClick={openEvidence} className="inline-flex items-center gap-1 text-primary-600 underline-offset-2 hover:underline dark:text-primary-400">
              <FileText className="h-4 w-4" aria-hidden="true" /> {c.evidence_name}
            </button>
            <p className="text-xs text-muted-foreground">
              Submitted {when(c.submitted_at)}{c.decided_at && ` · ${c.status === 'rejected' ? 'rejected' : 'approved'} ${when(c.decided_at)}`}
              {c.decision_note && ` — “${c.decision_note}”`}
              {c.revoked_at && ` · revoked ${when(c.revoked_at)} — “${c.revoke_note}”`}
            </p>
          </div>
        )}

        {canSubmit && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="overlap-reason">Why this is genuinely separate work (recorded with your name)</Label>
              <Textarea id="overlap-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Separate project contract for the website build, outside their job; accountant approved on 1 Nov 2026." />
              {reason && reasonShort && <p className="text-xs text-warning">At least {MIN_REASON} characters.</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="overlap-evidence">Evidence — the separate contract or accountant's approval (PDF, PNG or JPEG, up to 15 MB)</Label>
              <input id="overlap-evidence" type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-card file:px-3 file:py-1.5 file:text-sm file:text-foreground" />
              {fileError && <p className="text-xs text-danger">{fileError}</p>}
            </div>
          </div>
        )}

        {c?.status === 'pending' && canApprovePayroll && iSubmitted && (
          <p className="text-sm text-muted-foreground">You submitted this clearance, so a different approver must decide it.</p>
        )}
        {c && canApprovePayroll && (c.status === 'pending' || c.status === 'approved') && (
          <div className="space-y-1.5">
            <Label htmlFor="overlap-note">{c.status === 'pending' ? 'Note (required to reject)' : 'Why are you revoking it? (required)'}</Label>
            <Textarea id="overlap-note" rows={2} value={note} aria-invalid={noteMissing || undefined}
              onChange={(e) => { setNote(e.target.value); setNoteMissing(false); }} />
            {noteMissing && <p role="alert" className="text-xs text-danger">Add a note saying why</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {canSubmit && (
            <Button onClick={submit} disabled={busy || !file || !!fileError || reasonShort}>
              {busy ? 'Submitting…' : 'Submit for approval'}
            </Button>
          )}
          {c?.status === 'pending' && canApprovePayroll && (
            <>
              <Button variant="outline" onClick={() => act('reject')} disabled={busy}>Reject</Button>
              <Button onClick={() => act('approve')} disabled={busy || iSubmitted}>Approve</Button>
            </>
          )}
          {c?.status === 'approved' && canApprovePayroll && (
            <Button variant="outline" onClick={() => act('revoke')} disabled={busy}>Revoke</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EmployeeOverlapDialog;
